import { AfterViewInit, Component, DestroyRef, forwardRef, inject, signal, viewChild } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Client } from '../../models/client.model';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { catchError, debounceTime, map, merge, of, startWith, Subscription, switchMap } from 'rxjs';
import { ClientsService } from '../../services/clients.service';
import { AdminService } from '../../admin/services/admin.service';
import { RoleService } from '../../role/role.service';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { HumanPipe } from '../../shared/pipes/human.pipe';
import { IdentifierPipe } from '../../shared/pipes/identifier.pipe';
import { DatePipe, LowerCasePipe, NgFor, TitleCasePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { rxResource, takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { PaginationDirective } from '../../shared/directives/pagination.directive';
import { StarsComponent } from '../../shared/stars/stars';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { GoToDetailsDirective } from '../../shared/directives/go-to-details.directive';

@Component({
  selector: 'app-crm-clients',
  templateUrl: './crm-clients.component.html',
  styleUrl: './crm-clients.component.scss',
  imports: [
    DatePipe,
    PaginationDirective,
    FormsModule,
    forwardRef(() => GoToDetailsDirective),
    HumanPipe,
    IdentifierPipe,
    LowerCasePipe,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSort,
    MatSortHeader,
    MatTableModule,
    MatToolbarModule,
    NgFor,
    ReactiveFormsModule,
    RouterModule,
    StarsComponent,
    TitleCasePipe,
  ]
})
export class CrmClientsComponent implements AfterViewInit {
  displayedColumns: string[] = [
    'rut',
    'name',
    'email',
    'phone',
    'last_order_date',
    'rating',
  ];

  dataSource = new MatTableDataSource<Client>;

  clients = signal<Client[]>(null);

  searchControl: UntypedFormControl = new UntypedFormControl();

  page = 1;

  public pageSizes = [10, 25, 50, 100];

  private paginator = viewChild<MatPaginator>('paginator');

  totalPages = 1;

  totalData = 0;

  subs: Subscription[] = [];

  public isLoading = signal(false);

  count = 0;

  public sort = 'correlative_id desc';

  private sorter = viewChild<MatSort>(MatSort);

  private readonly adminService = inject(AdminService);

  private readonly clientsService = inject(ClientsService);

  private readonly destroyRef = inject(DestroyRef);

  public readonly roleService = inject(RoleService);
  
  company = this.adminService.local$.getValue();

  countsByRating = rxResource({
    stream: () => this.clientsService.countsByRating().pipe(map(response => response.body)),
  });

  ratingFilter = signal<number[]>([0, 1, 2, 3, 4, 5]);
  ratingFilter$ = toObservable(this.ratingFilter);

  constructor(
  ) { }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator();

    merge(
      this.dataSource.paginator?.page || [],
      this.searchControl.valueChanges,
      this.sorter().sortChange.asObservable(),
      this.ratingFilter$,
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(300),
        switchMap(() => {
          this.isLoading.set(true);
          return this.getTableData$(
            this.searchControl.value,
            (this.paginator()?.pageIndex ?? 0) + 1,
            (this.paginator()?.pageSize ?? 10),
          ).pipe(catchError(() => of(null)));
        }),
        map((data) => {
          this.isLoading.set(false);
          if (data == null) return [];

          this.totalData = data.body.total_count;
          this.totalPages = data.body.total_pages;
          this.clients.set(data.body.items);
          return data.body.items;
        }),
      )
      .subscribe((data) => {
        this.dataSource = new MatTableDataSource(data);
      });
  }

  private getTableData$(query: string, pageIndex: number, pageSize: number) {
    return this.clientsService.index(query, pageIndex, pageSize, this.sort, this.ratingFilter());
  }

  clearSearch() {
    this.searchControl.setValue('');
  }

  getMore() {
    this.page += 1;
    this.isLoading.set(true);
    this.getTableData$(
      this.searchControl.value,
      this.page,
      20,
    )
    .pipe(catchError(() => of(null)))
    .subscribe((data) => {
      this.isLoading.set(false);
      if (data == null) return [];
      this.totalData = data.body.total_count;
      this.totalPages = data.body.total_pages;
      this.clients.update(clients => [...clients, ...data.body.items]);
    });
  }

  onSortChange(event: any) {
    if (!event?.direction) {
      this.sort = 'updated_at desc';
      return;
    }

    this.sort = `${event.active} ${event.direction}`;
  }

  getPercentageForCount(count: number): number {
    const totalCount = this.countsByRating.value().reduce((acc, curr) => curr.count + acc, 0);
    if (totalCount === 0) {
      return 0;
    }
    return Math.round((count / totalCount) * 100);
  }

  ratingOn(rating: number): boolean {
    return this.ratingFilter().includes(rating);
  }

  toggleRatingFilter(rating: number) {
    if (this.ratingOn(rating)) {
      this.ratingFilter.update(filter => filter.filter(r => r !== rating));
    } else {
      this.ratingFilter.update(filter => [...filter, rating]);
    }
  }
}
